import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 200);
  };

  const navBg = scrolled ? 'bg-background-50/98 backdrop-blur-md border-b border-background-200/70' : 'bg-transparent';
  const textColor = scrolled ? 'text-foreground-950' : 'text-white';
  const logoColor = scrolled ? 'text-foreground-950' : 'text-white';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">

          <Link to="/" className={`text-lg md:text-xl font-heading font-bold tracking-tight whitespace-nowrap ${logoColor} transition-colors duration-300`}>
            Pandabuy<span className="font-light">.autos</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-semibold whitespace-nowrap ${textColor} hover:opacity-70 transition-opacity cursor-pointer`}
            >
              Home
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-semibold whitespace-nowrap ${textColor} hover:opacity-70 transition-opacity cursor-pointer`}
            >
              Spreadsheet
            </a>

            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap ${textColor} hover:opacity-70 transition-opacity cursor-pointer`}
              >
                Categories
                <span className={`w-3 h-3 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-background-50 rounded-lg border border-background-200/70 py-3 w-56 shadow-sm">
                  <div className="grid grid-cols-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <span className="w-4 h-4 flex items-center justify-center text-foreground-400">
                          <i className={`${cat.icon} text-sm`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/article/pandabuy-spreadsheet-guide"
              className={`text-sm font-semibold whitespace-nowrap ${textColor} hover:opacity-70 transition-opacity cursor-pointer`}
            >
              Shopping Guide
            </Link>

            <Link
              to="/article/pandabuy-spreadsheet-for-beginners"
              className={`text-sm font-semibold whitespace-nowrap ${textColor} hover:opacity-70 transition-opacity cursor-pointer`}
            >
              Blog
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 text-sm font-semibold rounded-full border whitespace-nowrap cursor-pointer transition-all duration-300 ${
                scrolled
                  ? 'border-foreground-300 text-foreground-800 hover:bg-foreground-50'
                  : 'border-white/50 text-white hover:bg-white/10'
              }`}
            >
              pandabuy sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-bold rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
            >
              Buy It Now
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>
          </div>

          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${scrolled ? 'text-foreground-950' : 'text-white'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={mobileMenuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-2">
            <Link
              to="/"
              className="text-sm font-semibold text-foreground-900 py-2.5 cursor-pointer flex items-center gap-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                <i className="ri-home-4-line text-sm"></i>
              </span>
              Home
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-foreground-900 py-2.5 cursor-pointer flex items-center gap-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                <i className="ri-grid-line text-sm"></i>
              </span>
              Spreadsheet
            </a>

            <div>
              <button
                className="flex items-center justify-between w-full text-sm font-semibold text-foreground-900 py-2.5 cursor-pointer"
                onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
              >
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                    <i className="ri-apps-2-line text-sm"></i>
                  </span>
                  Categories
                </span>
                <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileDropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-base"></i>
                </span>
              </button>
              {mobileDropdownOpen && (
                <div className="pl-6 pt-2 pb-1 grid grid-cols-2 gap-x-3 gap-y-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-1.5 py-1.5 text-xs text-foreground-600 hover:text-foreground-900 cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 text-primary-500">
                        <i className={`${cat.icon} text-[10px]`}></i>
                      </span>
                      <span className="truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/article/pandabuy-spreadsheet-guide"
              className="text-sm font-semibold text-foreground-900 py-2.5 cursor-pointer flex items-center gap-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                <i className="ri-book-open-line text-sm"></i>
              </span>
              Shopping Guide
            </Link>

            <Link
              to="/article/pandabuy-spreadsheet-for-beginners"
              className="text-sm font-semibold text-foreground-900 py-2.5 cursor-pointer flex items-center gap-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                <i className="ri-article-line text-sm"></i>
              </span>
              Blog
            </Link>

            <div className="flex flex-col gap-2 pt-3 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-2.5 text-sm font-semibold rounded-full border border-foreground-300 text-foreground-800 text-center cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                pandabuy sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-5 py-2.5 text-sm font-bold rounded-full bg-primary-500 text-white text-center cursor-pointer flex items-center justify-center gap-1.5"
                onClick={() => setMobileMenuOpen(false)}
              >
                Buy It Now
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </span>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}